import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { CarrotDownIcon, CarrotUpIcon } from 'components/mui';
import { CinchActionButtonText } from 'components';
import { useCinchNotes } from './use-cinch-notes';
import CinchNotesList from './cinch-notes-list.component';

/**
 * A reusable notes component that provides full CRUD functionality for additional notes.
 * Features include expandable/collapsible interface, inline editing, user avatars,
 * and smart display of creator vs editor information.
 *
 * @param {string} objectId - The ID of the object to attach notes to (required).
 *
 * @param {string} type - The table name or model of the object: (e.g., 'survey_invite', 'customer', 'transaction') (required).
 *
 * @param {string} title - The title displayed in the expandable header.
 *
 * @param {boolean} initialExpanded - Whether the notes section starts expanded.
 *
 * @param {function} onNotesChange - Callback function called when notes are added, updated, or deleted.
 *
 * @example
 * // Basic usage with survey responses (uses survey_invite type)
 * <CinchNotes objectId="uuid_1111-2222-3333-444444444444" type="survey_invite" />
 *
 * @example
 * // With custom title and callback for customer
 * <CinchNotes
 *   objectId="uuid_1111-2222-3333-444444444444"
 *   type="customer"
 *   title="Customer Notes"
 *   initialExpanded={false}
 *   onNotesChange={(notes) => console.log('Notes updated:', notes)}
 * />
 *
 * */

export default function CinchNotes({
  objectId,
  type,
  title = 'Notes & Actions',
  initialExpanded = false,
  onNotesChange = () => {}
}) {
  const [notesExpanded, setNotesExpanded] = useState(initialExpanded);
  const notesApi = useCinchNotes({
    objectId,
    type,
    onNotesChange
  });
  const {
    notes,
    startAdding
  } = notesApi;
  return <>
      <Stack direction={'row'} sx={{
      pt: 2,
      justifyContent: 'space-between'
    }}>
        <Box id="notes-actions-button-container">
          <Button onClick={() => setNotesExpanded(prev => !prev)} sx={{
          width: '100%',
          justifyContent: 'space-between',
          borderRadius: '20px',
          textTransform: 'none',
          padding: '16px'
        }} className={`notes-actions-button${notesExpanded ? ' expanded' : ''}`}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{
            flex: 1
          }}>
              <Typography variant="tableHeader">{`${title} ${notes?.length > 0 ? '(' + notes?.length + ')' : ''}`}</Typography>
              {notesExpanded ? <CarrotUpIcon /> : <CarrotDownIcon />}
            </Stack>
          </Button>
        </Box>
        <Box id="add-new-note-container" sx={{
        display: notesExpanded ? 'block' : 'none',
        mt: 2
      }}>
          {notesExpanded && <CinchActionButtonText onClick={e => {
          e.stopPropagation();
          startAdding();
        }} size="small" text="Add Note" />}
        </Box>
      </Stack>

      {notesExpanded && <Box sx={{
      mt: 2
    }}>
          <CinchNotesList notesApi={notesApi} />
        </Box>}
    </>;
}
CinchNotes.propTypes = {
  /** The ID of the object to attach notes to (required). */
  objectId: PropTypes.string.isRequired,
  /** The type/table name of the object (e.g., 'survey', 'customer', 'transaction') (required). */
  type: PropTypes.string.isRequired,
  /** The title displayed in the expandable header. */
  title: PropTypes.string,
  /** Whether the notes section starts expanded. */
  initialExpanded: PropTypes.bool,
  /** Callback function called when notes are added, updated, or deleted. */
  onNotesChange: PropTypes.func
};
CinchNotes.defaultProps = {
  title: 'Notes & Actions',
  initialExpanded: false,
  onNotesChange: () => {}
};